declare const module: {
	hot?: unknown;
};

/** Supported coordinate input-mode codes supplied by field configuration. */
export const COORDINATE_MODE_CODES = {
	decimalDegrees: "DECIMAL_DEGREES",
	degreesDecimalMinutes: "DEGREES_DECIMAL_MINUTES"
} as const;

/** A supported coordinate input-mode code. */
export type CoordinateModeCode = typeof COORDINATE_MODE_CODES[keyof typeof COORDINATE_MODE_CODES];

/** Describes a coordinate input mode presented by the mode switcher. */
export interface CoordinateModeDefinition {
	code: CoordinateModeCode;
	label: string;
}

/** Field configuration used to select or restrict coordinate input modes. */
export interface CoordinateModeField {
	Mode?: string;
	DefaultMode?: string;
}

/** The resolved input-mode state for a coordinate field. */
export interface ResolvedCoordinateModes {
	activeMode: CoordinateModeCode;
	availableModes: CoordinateModeDefinition[];
	canSwitch: boolean;
	isUnsupportedFixedMode: boolean;
}

/** Enables mode switching for unconfigured coordinate fields in the local webpack development server. */
const ENABLE_MODE_SWITCHING_PREVIEW = true;
const IS_MODE_SWITCHING_PREVIEW_ENABLED = ENABLE_MODE_SWITCHING_PREVIEW && Boolean(module.hot);

/** Coordinate modes available to configured switchable fields. */
export const COORDINATE_MODE_DEFINITIONS: readonly CoordinateModeDefinition[] = [
	{
		code: COORDINATE_MODE_CODES.decimalDegrees,
		label: "Decimal Degrees"
	},
	{
		code: COORDINATE_MODE_CODES.degreesDecimalMinutes,
		label: "Degrees Decimal Minutes"
	}
];

/**
 * Determines whether a field configuration value identifies a supported coordinate mode.
 *
 * @param value - Mode code read from field configuration.
 * @returns Whether the value is a supported coordinate mode code.
 */
function isSupportedCoordinateMode(value: string | undefined): value is CoordinateModeCode {
	return value === COORDINATE_MODE_CODES.decimalDegrees
		|| value === COORDINATE_MODE_CODES.degreesDecimalMinutes;
}

/**
 * Gets the next available coordinate mode, wrapping to the first mode at the end of the cycle.
 *
 * @param currentMode - Currently active coordinate mode.
 * @param availableModes - Ordered modes through which the switcher can rotate.
 * @returns The next mode code, or the current mode when fewer than two modes are available.
 */
export function getNextCoordinateMode(
	currentMode: CoordinateModeCode,
	availableModes: readonly CoordinateModeDefinition[] = COORDINATE_MODE_DEFINITIONS
): CoordinateModeCode {
	if(availableModes.length < 2) {
		return currentMode;
	}

	const currentIndex = availableModes.findIndex(mode => mode.code === currentMode);
	const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % availableModes.length;

	return availableModes[nextIndex].code;
}

/**
 * Resolves the active and available coordinate modes from the field configuration contract.
 *
 * A configured `Mode` fixes the field to one mode. A configured `DefaultMode` selects the
 * initial mode while allowing switching. An unconfigured field retains the legacy Decimal
 * Degrees behavior unless the local development preview is enabled.
 *
 * @param field - Coordinate field mode configuration.
 * @returns Resolved mode state consumed by the coordinate controller.
 */
export function resolveCoordinateModes(field: CoordinateModeField = {}): ResolvedCoordinateModes {
	const configuredMode = field.Mode;
	const configuredDefaultMode = field.DefaultMode;
	const mode = isSupportedCoordinateMode(configuredMode) ? configuredMode : undefined;
	const defaultMode = isSupportedCoordinateMode(configuredDefaultMode) ? configuredDefaultMode : undefined;
	const hasFixedMode = typeof configuredMode === "string" && configuredMode !== "";
	const hasDefaultMode = typeof configuredDefaultMode === "string" && configuredDefaultMode !== ""
		|| IS_MODE_SWITCHING_PREVIEW_ENABLED;
	const activeMode = mode ?? defaultMode ?? COORDINATE_MODE_CODES.decimalDegrees;

	return {
		activeMode,
		availableModes: hasFixedMode
			? COORDINATE_MODE_DEFINITIONS.filter(item => item.code === activeMode)
			: hasDefaultMode
				? [...COORDINATE_MODE_DEFINITIONS]
				: COORDINATE_MODE_DEFINITIONS.filter(item => item.code === COORDINATE_MODE_CODES.decimalDegrees),
		canSwitch: !hasFixedMode && hasDefaultMode && COORDINATE_MODE_DEFINITIONS.length > 1,
		isUnsupportedFixedMode: hasFixedMode && mode === undefined
	};
}
